import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="lrk-bulk-selection"
export default class extends Controller {
  static targets = [
    "checkbox",
    "actionBar",
    "selectedText",
    "placesText",
    "bulkForm",
    "selectAllBtn",
    "clearAllBtn",
    "selectionBanner",
    "bannerText",
    "selectAllLink",
    "selectionMode"
  ];

  static values = {
    totalLocations: Number,
    pageSize: Number,
    searchQuery: String
  };

  connect() {
    // Track selection mode: 'individual', 'page', or 'all'
    this.selectionMode = 'individual';
    this.updateActionBar();
  }

  updateActionBar() {
    const selectedCheckboxes = this.checkboxTargets.filter((checkbox) =>
      checkbox.checked && !checkbox.disabled
    );
    const selectedCount = selectedCheckboxes.length;
    const availableCheckboxes = this.checkboxTargets.filter(checkbox => !checkbox.disabled);
    const allPageSelected = availableCheckboxes.length > 0 &&
      availableCheckboxes.every(checkbox => checkbox.checked);

    // Update action bar visibility and content
    if (selectedCount > 0 || this.selectionMode === 'all') {
      this.actionBarTarget.classList.remove("hidden");

      // Update text based on selection mode
      if (this.selectionMode === 'all') {
        this.selectedTextTarget.textContent = this.#pluralizeLocations(this.totalLocationsValue);
        // For "all" mode, calculate estimated total places (current page average * total)
        const avgPlacesPerLocation = selectedCheckboxes.length > 0 ?
          selectedCheckboxes.reduce((sum, checkbox) => {
            return sum + (parseInt(checkbox.dataset.numberOfPlaces, 10) || 0);
          }, 0) / selectedCheckboxes.length : 0;
        const estimatedTotalPlaces = Math.round(avgPlacesPerLocation * this.totalLocationsValue);
        this.placesTextTarget.textContent = this.#pluralizePlaces(estimatedTotalPlaces);
      } else {
        this.selectedTextTarget.textContent = this.#pluralizeLocations(selectedCount);
        // Calculate actual places for selected items
        const totalPlaces = selectedCheckboxes.reduce((sum, checkbox) => {
          const places = parseInt(checkbox.dataset.numberOfPlaces, 10) || 0;
          return sum + places;
        }, 0);
        this.placesTextTarget.textContent = this.#pluralizePlaces(totalPlaces);
      }
    } else {
      this.actionBarTarget.classList.add("hidden");
    }

    // Update selection banner
    this.#updateSelectionBanner(allPageSelected, selectedCount, availableCheckboxes.length);

    // Update button states
    if (this.hasSelectAllBtnTarget) {
      this.selectAllBtnTarget.disabled = allPageSelected || availableCheckboxes.length === 0;
    }

    if (this.hasClearAllBtnTarget) {
      this.clearAllBtnTarget.disabled = selectedCount === 0 && this.selectionMode !== 'all';
    }

    // Update hidden form fields
    if (this.hasSelectionModeTarget) {
      this.selectionModeTarget.value = this.selectionMode;
    }
  }

  selectPage() {
    this.checkboxTargets
      .filter(checkbox => !checkbox.disabled)
      .forEach(checkbox => {
        checkbox.checked = true;
      });
    this.selectionMode = 'page';
    this.updateActionBar();
  }

  selectAllResults() {
    // Select all available checkboxes on current page
    this.checkboxTargets
      .filter(checkbox => !checkbox.disabled)
      .forEach(checkbox => {
        checkbox.checked = true;
      });
    this.selectionMode = 'all';
    this.updateActionBar();
  }

  clearAll() {
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = false;
    });
    this.selectionMode = 'individual';
    this.updateActionBar();
  }

  bulkAdd(event) {
    event.preventDefault();

    if (this.selectionMode === 'all') {
      // For "all" mode, we don't need individual IDs
      // The controller will handle this by re-running the search
      if (!this.searchQueryValue) {
        alert("Geen zoekopdracht gevonden.");
        return;
      }
    } else {
      // For individual/page mode, collect selected IDs
      const selectedIds = this.checkboxTargets
        .filter((checkbox) => checkbox.checked && !checkbox.disabled)
        .map((checkbox) => checkbox.value);

      if (selectedIds.length === 0) {
        alert("Selecteer eerst één of meer locaties.");
        return;
      }

      // Clear existing hidden inputs
      const existingInputs = this.bulkFormTarget.querySelectorAll('input[name="lrk_location_ids[]"]');
      existingInputs.forEach(input => input.remove());

      // Add selected IDs as hidden inputs
      selectedIds.forEach(id => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "lrk_location_ids[]";
        input.value = id;
        this.bulkFormTarget.appendChild(input);
      });
    }

    this.bulkFormTarget.requestSubmit();
  }

  disconnect() {
    // Clean up any event listeners if needed
  }

  // Private helper methods for pluralization
  #pluralizeLocations(count) {
    if (count === 0) return "Geen locaties geselecteerd";
    if (count === 1) return "1 locatie geselecteerd";
    return `${count} locaties geselecteerd`;
  }

  #pluralizePlaces(count) {
    if (count === 0) return "geen kindplaatsen";
    if (count === 1) return "1 kindplaats totaal";
    return `${count} kindplaatsen totaal`;
  }

  #updateSelectionBanner(allPageSelected, selectedCount, availableCount) {
    if (!this.hasSelectionBannerTarget) return;

    // Show banner only if:
    // 1. All items on page are selected AND
    // 2. There are more results available (more pages) AND
    // 3. We're not already in "all" mode
    const shouldShowBanner = allPageSelected &&
                            this.totalLocationsValue > this.pageSizeValue &&
                            this.selectionMode !== 'all';

    if (shouldShowBanner) {
      this.selectionBannerTarget.classList.remove("hidden");

      if (this.hasBannerTextTarget) {
        this.bannerTextTarget.textContent = `Alle ${availableCount} locaties op deze pagina zijn geselecteerd.`;
      }

      if (this.hasSelectAllLinkTarget) {
        this.selectAllLinkTarget.textContent = `Selecteer alle ${this.totalLocationsValue} locaties uit je zoekopdracht`;
        this.selectAllLinkTarget.style.display = "inline";
      }
    } else if (this.selectionMode === 'all') {
      this.selectionBannerTarget.classList.remove("hidden");

      if (this.hasBannerTextTarget) {
        this.bannerTextTarget.textContent = `Alle ${this.totalLocationsValue} locaties uit je zoekopdracht zijn geselecteerd.`;
      }

      if (this.hasSelectAllLinkTarget) {
        this.selectAllLinkTarget.style.display = "none";
      }
    } else {
      this.selectionBannerTarget.classList.add("hidden");
    }
  }
}